import React, { useEffect, useMemo, useState } from "react";
import {
  CalendarDays,
  CheckCircle2,
  Download,
  Eye,
  RefreshCw,
  Search,
  XCircle
} from "lucide-react";
import API, { getMediaUrl } from "../services/api";
import AdminLayout from "./AdminLayout";

function AdminDeposit() {
  const [requests, setRequests] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [query, setQuery] = useState("");
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().slice(0, 10));

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const res = await API.get("/admin/deposits");
      setRequests(res.data || []);
    } catch (err) {
      console.log(err);
      alert("Error loading deposit requests");
    }
  };

  const approve = async (deposit) => {
    try {
      await API.post("/admin/approve-deposit", deposit);
      loadData();
    } catch (err) {
      alert(err?.response?.data?.message || "Unable to approve deposit");
    }
  };

  const reject = async (id) => {
    try {
      await API.post("/admin/reject-deposit", { id });
      loadData();
    } catch (err) {
      alert(err?.response?.data?.message || "Unable to reject deposit");
    }
  };

  const filtered = useMemo(() => {
    const search = query.trim().toLowerCase();
    return requests.filter((req) => {
      const status = String(req.status || "pending").toLowerCase();
      const matchesFilter = filter === "all" || status === filter;
      const matchesSearch = !search || [
        req.id, req.user_name, req.user_phone, req.amount, req.utr
      ].filter(Boolean).some((v) => String(v).toLowerCase().includes(search));
      const matchesDate = !selectedDate || (req.created_at || "").slice(0, 10) === selectedDate;
      return matchesFilter && matchesSearch && matchesDate;
    });
  }, [filter, query, requests, selectedDate]);

  const summaryRows = useMemo(() => {
    return requests.filter((req) => !selectedDate || (req.created_at || "").slice(0, 10) === selectedDate);
  }, [requests, selectedDate]);

  const pendingCount = countByStatus(summaryRows, "pending");
  const approvedCount = countByStatus(summaryRows, "approved");
  const rejectedCount = countByStatus(summaryRows, "rejected");

  const exportCsv = () => {
    downloadCsv("deposits.csv", [
      ["Request ID", "Date", "User", "Mobile", "Amount", "UTR", "Status"],
      ...filtered.map((req) => [
        req.id,
        formatDateTime(req.created_at),
        req.user_name || "User",
        req.user_phone || "",
        Number(req.amount || 0),
        req.utr || "",
        req.status || ""
      ])
    ]);
  };

  return (
    <AdminLayout>
      <section style={page}>
        <header style={pageHeader}>
          <div>
            <h1 style={title}>Deposit Management</h1>
            <p style={subtitle}>Manage deposit requests from users</p>
          </div>
          <div style={headerActions}>
            <button type="button" style={iconButton} onClick={loadData}><RefreshCw size={14} /></button>
            <button type="button" style={exportButton} onClick={exportCsv}><Download size={14} /> Export</button>
          </div>
        </header>

        <section style={summaryPanel}>
          <div style={summaryGrid}>
            <SummaryCard label="Pending" value={pendingCount} foot={formatMoney(sumByStatus(summaryRows, "pending"))} color="#fde68a" bg="rgba(245,158,11,0.25)" onClick={() => setFilter("pending")} />
            <SummaryCard label="Approved" value={approvedCount} foot={formatMoney(sumByStatus(summaryRows, "approved"))} color="#6ee7b7" bg="rgba(16,185,129,0.2)" onClick={() => setFilter("approved")} />
            <SummaryCard label="Rejected" value={rejectedCount} foot={formatMoney(sumByStatus(summaryRows, "rejected"))} color="#fca5a5" bg="rgba(239,68,68,0.2)" onClick={() => setFilter("rejected")} />
            <SummaryCard label="Total" value={summaryRows.length} foot={formatMoney(sumAll(summaryRows))} color="#fed7aa" bg="rgba(99,102,241,0.25)" onClick={() => setFilter("all")} />
          </div>
        </section>

        <div style={toolbar}>
          <div style={searchBox}>
            <Search size={14} color="#94a3b8" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search user, ID, mobile..."
              style={searchInput}
            />
          </div>
          <label style={datePicker}>
            <CalendarDays size={13} />
            <input type="date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} style={dateInput} />
          </label>
          {selectedDate && (
            <button type="button" style={clearDateButton} onClick={() => setSelectedDate("")}>All dates</button>
          )}
        </div>

        <div style={filterRow}>
          {[
            ["pending", "Pending", pendingCount],
            ["approved", "Approved", approvedCount],
            ["rejected", "Rejected", rejectedCount],
            ["all", "All", requests.length]
          ].map(([key, label, count]) => (
            <button key={key} type="button" style={filter === key ? activeFilter : filterButton} onClick={() => setFilter(key)}>
              {label} {key === "pending" && <span style={countDot}>{count}</span>}
            </button>
          ))}
        </div>

        <div style={tableWrap}>
          <table style={table}>
            <thead>
              <tr>
                <th style={th}>Request ID</th>
                <th style={th}>User</th>
                <th style={th}>Amount</th>
                <th style={th}>UTR / Screenshot</th>
                <th style={th}>Status</th>
                <th style={{ ...th, textAlign: "center" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((req) => (
                <tr key={req.id} style={tr}>
                  <td style={td}>
                    <strong>{req.id}</strong>
                    <small>{formatDateTime(req.created_at)}</small>
                  </td>
                  <td style={td}>
                    <div style={userCell}>
                      <span style={avatar}>{getInitial(req.user_name)}</span>
                      <div>
                        <strong style={userName}>{req.user_name || "User"}</strong>
                        <div style={userMeta}>{req.user_phone || "-"}</div>
                      </div>
                    </div>
                  </td>
                  <td style={td}><strong>{formatMoney(req.amount)}</strong></td>
                  <td style={td}>
                    <div style={paymentBox}>
                      {req.utr && <span style={utrBadge}>UTR: {req.utr}</span>}
                      {req.screenshot && (
                        <a href={getMediaUrl(`/uploads/${req.screenshot}`)} target="_blank" rel="noreferrer" style={screenshotLink}>
                          <Eye size={12} /> View Screenshot
                        </a>
                      )}
                      {!req.utr && !req.screenshot && <small style={{ color: "#94a3b8" }}>No proof attached</small>}
                    </div>
                  </td>
                  <td style={td}><StatusBadge status={req.status} /></td>
                  <td style={td}>
                    <div style={actions}>
                      {req.status === "pending" ? (
                        <>
                          <button type="button" style={approveButton} onClick={() => approve(req)}>
                            <CheckCircle2 size={14} /> Approve
                          </button>
                          <button type="button" style={rejectButton} onClick={() => reject(req.id)}>
                            <XCircle size={14} /> Reject
                          </button>
                        </>
                      ) : (
                        <span style={{ fontSize: 12, color: "#94a3b8" }}>
                          {req.status === "approved" ? "Approved" : "Rejected"}
                        </span>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td style={emptyCell} colSpan="6">No deposit requests found</td>
                </tr>
              )}
            </tbody>
          </table>
          <footer style={tableFooter}>
            <span>1-{filtered.length} of {filtered.length}</span>
          </footer>
        </div>
      </section>
    </AdminLayout>
  );
}

function SummaryCard({ label, value, foot, color, bg, onClick }) {
  return (
    <button type="button" style={{ ...summaryCard, background: bg, borderColor: color }} onClick={onClick}>
      <span style={{ color }}>{label}</span>
      <strong>{value}</strong>
      <small style={{ color }}>{foot}</small>
    </button>
  );
}

function StatusBadge({ status }) {
  const map = {
    pending: { bg: "#fef3c7", color: "#d97706", label: "Pending" },
    approved: { bg: "#dcfce7", color: "#16a34a", label: "Approved" },
    rejected: { bg: "#fee2e2", color: "#dc2626", label: "Rejected" }
  };
  const s = map[status] || map.pending;
  return <span style={{ background: s.bg, color: s.color, borderRadius: 6, padding: "3px 10px", fontSize: 12, fontWeight: 700 }}>{s.label}</span>;
}

function countByStatus(requests, status) {
  return requests.filter((r) => String(r.status || "pending").toLowerCase() === status).length;
}

function sumByStatus(requests, status) {
  return requests.filter((r) => String(r.status || "pending").toLowerCase() === status).reduce((s, r) => s + Number(r.amount || 0), 0);
}

function sumAll(requests) {
  return requests.reduce((s, r) => s + Number(r.amount || 0), 0);
}

function formatMoney(value) {
  return `₹${Number(value || 0).toLocaleString("en-IN")}`;
}

function formatDateTime(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("en-IN", { day: "2-digit", month: "short", year: "2-digit", hour: "2-digit", minute: "2-digit" });
}

function downloadCsv(filename, rows) {
  const csv = rows.map((row) => row.map((cell) => `"${String(cell ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

function getInitial(name) {
  return (name || "U").trim().charAt(0).toUpperCase();
}

const page = { color: "#111827" };
const pageHeader = { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 };
const title = { margin: 0, fontSize: 22, fontWeight: 800, color: "#431407" };
const subtitle = { margin: "4px 0 0", color: "#64748b", fontSize: 13 };
const headerActions = { display: "flex", gap: 6 };
const iconButton = { width: 32, height: 32, display: "grid", placeItems: "center", border: "1px solid #e5e7eb", background: "#fff", color: "#f97316", cursor: "pointer", borderRadius: 8 };
const exportButton = { height: 32, padding: "0 16px", display: "flex", alignItems: "center", gap: 7, border: 0, background: "#22c55e", color: "#fff", fontWeight: 700, fontSize: 12, cursor: "pointer", borderRadius: 8 };
const summaryPanel = { background: "linear-gradient(135deg, #431407, #7c2d12)", borderRadius: 16, padding: "16px", marginBottom: 16 };
const summaryGrid = { display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 10 };
const summaryCard = { minHeight: 70, border: "1px solid rgba(255,255,255,0.15)", borderRadius: 12, padding: "12px 14px", display: "flex", flexDirection: "column", gap: 4, textAlign: "left", cursor: "pointer", color: "#fff", background: "rgba(255,255,255,0.08)" };
const toolbar = { display: "flex", gap: 6, marginBottom: 12, alignItems: "center" };
const searchBox = { height: 38, display: "flex", alignItems: "center", gap: 8, border: "1.5px solid #e5e7eb", background: "#fff", borderRadius: 12, padding: "0 12px", flex: 1, maxWidth: 320 };
const searchInput = { border: 0, outline: "none", flex: 1, fontSize: 13 };
const datePicker = { height: 38, display: "flex", alignItems: "center", gap: 6, border: "1.5px solid #e5e7eb", background: "#fff", borderRadius: 12, padding: "0 12px", color: "#64748b", fontSize: 12, cursor: "pointer" };
const dateInput = { border: 0, outline: "none", fontSize: 12, color: "#0f172a", cursor: "pointer" };
const clearDateButton = { height: 38, padding: "0 12px", border: "1.5px solid #e5e7eb", background: "#fff", borderRadius: 12, fontSize: 12, cursor: "pointer", color: "#64748b" };
const filterRow = { display: "flex", gap: 6, marginBottom: 12 };
const filterButton = { height: 34, padding: "0 14px", border: "1.5px solid #e5e7eb", background: "#fff", borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: "pointer", color: "#64748b", display: "flex", alignItems: "center", gap: 6 };
const activeFilter = { ...filterButton, background: "#431407", color: "#fff", border: "1.5px solid #431407" };
const countDot = { background: "#f97316", color: "#fff", borderRadius: 999, padding: "1px 6px", fontSize: 10, fontWeight: 800 };
const tableWrap = { background: "#fff", border: "1.5px solid #e2e8f0", borderRadius: 14, overflow: "hidden" };
const table = { width: "100%", borderCollapse: "collapse" };
const th = { padding: "12px 14px", textAlign: "left", fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.05em", background: "#f8fafc", borderBottom: "1px solid #e2e8f0" };
const tr = { borderBottom: "1px solid #f1f5f9" };
const td = { padding: "12px 14px", fontSize: 13, color: "#334155", verticalAlign: "top", display: "table-cell" };
const userCell = { display: "flex", alignItems: "flex-start", gap: 10 };
const avatar = { width: 30, height: 30, borderRadius: "50%", background: "#f1f5f9", display: "grid", placeItems: "center", fontSize: 12, fontWeight: 800, color: "#475569", flexShrink: 0 };
const userName = { fontSize: 13, display: "block", color: "#0f172a" };
const userMeta = { fontSize: 11, color: "#94a3b8", marginTop: 2 };
const paymentBox = { display: "flex", flexDirection: "column", gap: 3 };
const utrBadge = { fontSize: 11, background: "#dcfce7", color: "#15803d", borderRadius: 5, padding: "2px 7px", display: "inline-block", width: "fit-content" };
const screenshotLink = { fontSize: 11, color: "#16a34a", display: "flex", alignItems: "center", gap: 4, textDecoration: "none" };
const actions = { display: "flex", flexDirection: "column", gap: 5, alignItems: "flex-start" };
const approveButton = { height: 30, padding: "0 12px", border: 0, background: "#16a34a", color: "#fff", borderRadius: 7, fontSize: 12, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", gap: 5 };
const rejectButton = { height: 30, padding: "0 12px", border: 0, background: "#dc2626", color: "#fff", borderRadius: 7, fontSize: 12, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", gap: 5 };
const emptyCell = { padding: "40px 16px", textAlign: "center", color: "#94a3b8", fontSize: 13 };
const tableFooter = { padding: "10px 16px", borderTop: "1px solid #e2e8f0", fontSize: 12, color: "#94a3b8" };

export default AdminDeposit;
